/**
 * useDragAndDrop Hook
 * 
 * 处理聊天界面的拖拽文件上传功能
 * 
 * 功能特点：
 * - 管理拖拽状态（isDragging）
 * - 处理拖拽事件（dragover, dragleave, drop）
 * - 文件验证和错误处理（复用现有机制）
 * - 与现有Uppy上传流程无缝集成
 * 
 * 技术实现：
 * - 使用现有的 enqueueFilesAtom 添加文件到上传队列
 * - 复用 validateFileTypeAndSize 进行文件验证
 * - 复用现有的错误提示机制（Chakra UI Toast）
 */
import { useState, useCallback } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { createStandaloneToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { enqueueFilesAtom, uppyInstanceAtom } from '../../atoms/v3/uppyAtoms';
import { validateFileTypeAndSize } from '../../utils/fileValidation';
import { trackEvent } from '../../lib/analytics';

// 创建独立的 toast 实例
const { toast } = createStandaloneToast();

interface UseDragAndDropReturn {
  /** 是否正在拖拽 */
  isDragging: boolean;
  /** 处理拖拽悬停事件 */
  handleDragOver: (e: React.DragEvent) => void;
  /** 处理拖拽离开事件 */
  handleDragLeave: (e: React.DragEvent) => void;
  /** 处理文件释放事件 */
  handleDrop: (e: React.DragEvent) => void;
}

export const useDragAndDrop = (): UseDragAndDropReturn => {
  const [isDragging, setIsDragging] = useState(false);
  const uppy = useAtomValue(uppyInstanceAtom);
  const enqueueFiles = useSetAtom(enqueueFilesAtom);
  const { t } = useTranslation(['v3/chat', 'common']);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // 检查是否包含文件
    if (e.dataTransfer.types.includes('Files')) {
      if (!isDragging) {
        setIsDragging(true);
      }
    }
  }, [isDragging]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // 只有当离开最外层容器时才重置状态
    // 避免子元素触发误判
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (!uppy) {
      console.warn('拖拽上传失败: Uppy实例不存在');
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) {
      return;
    }

    // 处理每个文件，先验证再添加到队列
    const validFiles: File[] = [];

    files.forEach((file: File) => {
      try {
        // 复用现有的文件验证逻辑
        validateFileTypeAndSize({
          type: file.type,
          size: file.size,
          name: file.name
        });

        // 验证通过，添加到有效文件列表
        validFiles.push(file);
      } catch (error) {
        // 复用现有的错误处理逻辑
        const errorMessage = error instanceof Error ? error.message : '';
        if (errorMessage.includes('不支持的文件类型')) {
          // 记录用户尝试上传不支持的文件类型
          const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'unknown';
          const fileType = file.type || 'unknown';

          // 发送埋点事件
          trackEvent('file upload unsupported type', {
            file_extension: fileExtension,
            file_type: fileType,
            file_size: file.size,
            error_message: errorMessage,
            upload_source: 'drag-drop'
          });

          // 显示友好的错误提示
          toast({
            title: t('common:errors.invalidFileType'),
            description: t('common:errors.invalidFileTypeDescription'),
            status: 'error',
            duration: 5000,
            isClosable: true,
            position: 'top'
          });
        } else {
          // 其他错误（如文件大小超限）
          toast({
            title: t('common:errors.fileUploadError'),
            description: errorMessage,
            status: 'error',
            duration: 5000,
            isClosable: true,
            position: 'top'
          });
        }
      }
    });

    // 只将有效文件添加到队列
    if (validFiles.length > 0) {
      // 记录成功验证的文件类型
      validFiles.forEach(file => {
        const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'unknown';
        trackEvent('file upload valid type', {
          file_extension: fileExtension,
          file_type: file.type || 'unknown',
          file_size: file.size,
          upload_source: 'drag-drop'
        });
      });

      // 将File对象转换为MinimalRequiredUppyFileType格式
      const uppyFiles = validFiles.map(file => ({
        name: file.name,
        type: file.type,
        data: file,
        meta: {
          name: file.name,
          type: file.type,
          source: 'drag-drop'
        }
      }));

      // 使用现有的文件入队机制
      enqueueFiles(uppyFiles);
    }
  }, [uppy, enqueueFiles, t]);

  return {
    isDragging,
    handleDragOver,
    handleDragLeave,
    handleDrop
  };
};
